import {
  LitElement,
  html,
  css,
  map,
  range,
  paths,
  unsafeHTML,
  classMap,
  t,
} from '@hh/global.js'

class HHGalleryNav extends LitElement {
  static properties = {
    imageCount: {
      type: Number,
      attribute: 'image-count',
    },
    imageIds: {
      type: Array,
      attribute: 'image-ids',
    },
    videoIds: {
      type: Array,
      attribute: 'video-ids',
    },
    singleMedia: {
      type: Boolean,
      attribute: 'single-media',
    },
    modalId: {
      type: String,
      attribute: 'modal-id',
    },
    currentMedia: {
      type: String,
    },
  }

  constructor() {
    super()
  }

  get mediaItems() {
    return document.querySelectorAll('[data-media-item]') ?? []
  }

  connectedCallback() {
    super.connectedCallback()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.mediaIntersectionObserver?.disconnect()
  }

  firstUpdated() {
    this.mediaIntersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.updateNav(entry.target)
          }
        })
      },
      {
        threshold: 0.5,
      }
    )

    this.mediaItems.forEach((item) => {
      this.mediaIntersectionObserver.observe(item)
    })
  }

  updateNav(target) {
    const id = target.getAttribute('data-media-item')
    if (!id) return
    this.currentMedia = id
  }

  onPaginationClick(e, id) {
    e.preventDefault()
    const target = document.querySelector(`[data-media-item="${id}"]`)
    if (!target) return
    target.scrollIntoView({
      behavior: 'smooth',
    })
  }

  render() {
    return html`
      <div class="container">
        <ul class="nav-wrapper">
          ${map(this.imageIds, (id, idx) => {
            return html`
              <li
                class=${classMap({
                  'nav-item': true,
                  hide: this.singleMedia,
                })}
              >
                <a
                  class=${classMap({
                    'nav-link': true,
                    video: this.videoIds.includes(id),
                    active: this.currentMedia === id.toString(),
                  })}
                  aria-label="${t('accessibility.go_to')}
                ${t('accessibility.image')} ${idx + 1}"
                  @click=${(e) => this.onPaginationClick(e, id)}
                >
                  ${this.videoIds.includes(id)
                    ? unsafeHTML(paths.playExternalOutline)
                    : ''}
                </a>
              </li>
            `
          })}
          <li class="nav-item">
            <hh-broadcast
              on="click"
              event="modal"
              data='{
              "id": "${this.modalId}",
              "custom_event": "zoom:open"
            }'
              aria-haspopup="dialog"
            >
              <button
                class=${classMap({
                  zoom: true,
                  active: false,
                  'zoom--single': this.singleMedia,
                })}
                aria-label="${t('products.product.media.open_featured_media')}"
              >
                ${unsafeHTML(paths.zoom)}
              </button>
            </hh-broadcast>
          </li>
        </ul>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    :host {
      position: sticky;
      height: 100%;
      top: 40%;
      bottom: 0;
      z-index: 2;
      display: inline-flex;
      width: 0;
      z-index: 3;
      display: none;
    }

    :host([single-media]) {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      height: 0;
    }

    @media (min-width: 1024px) {
      :host {
        display: block;
      }
    }

    .container {
      margin-left: var(--hh-spacing-xl);
    }

    .nav-wrapper {
      padding: var(--hh-spacing-sm) var(--hh-spacing-xxs);
      box-shadow: var(--hh-md-box-shadow);
      border-radius: var(--hh-spacing-2xl);
      background-color: rgba(255, 255, 255, 0.9);
      width: 2.2rem;
      list-style: none;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--hh-spacing-xs);
      margin: 0;
    }

    .nav-link,
    .zoom {
      opacity: 0.5;
      transition: opacity var(--hh-duration-medium-long) ease;
      cursor: pointer;
    }

    .nav-link.active,
    .zoom.active,
    .zoom.zoom--single {
      opacity: 1;
    }

    .nav-link {
      display: block;
      width: 0.5rem;
      height: 0.5rem;
      background-color: var(--hh-color-blue);
      border-radius: var(--hh-spacing-2xl);
    }

    .zoom {
      appearance: none;
      border: none;
      background-color: transparent;
      padding: 0;
      margin: 0;
      display: flex;
      color: var(--hh-color-blue);
    }

    .zoom .icon,
    .video .icon {
      width: 1.4rem;
      height: 1.4rem;
    }

    .nav-link.video {
      display: flex;
      width: 100%;
      height: 100%;
      color: var(--hh-color-blue);
      background-color: transparent;
    }

    .hide {
      display: none;
    }
  `
}

if (!customElements.get('hh-gallery-nav')) {
  customElements.define('hh-gallery-nav', HHGalleryNav);
}